import { useEffect, useState, type ReactNode } from 'react'
import { PremiumGate } from '../../platform/access/PremiumGate'
import { ButtonPreview } from '../../platform/primitives/ButtonPreview'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { relinkIdOf, type InstitutionGroup } from './connectionGroups'
import { useAddAccountGate } from './useAccounts'
import { useReconnect } from './useRelinkOutcome'

/** Shared by the live chip and its `ButtonPreview` in the locked gate (#578). */
const RECONNECT_VARIANT = 'chip'
const RECONNECT_SIZE = 'panel'
const RECONNECT_LABEL = 'Reconnect'

/**
 * "Reconnect" on a DISCONNECTED bank — one component for the `/connections`
 * card and the `/accounts` Institutions panel row.
 *
 * Renders only when `relinkIdOf(group)` is set: the group is disconnected AND
 * the server said it is `reconnectable`. `connection_status === 'disconnected'`
 * alone is not enough — a bank with nothing left to reconnect to has no button.
 *
 * **Gated like linking**: the same `useAddAccountGate()` and `PremiumGate` on
 * `unlimited_accounts` that "Link a bank" uses. A locked chip is the gate's
 * preview — inert — so no request leaves for a user who may not add a bank.
 *
 * **A chip, not a fill** (rule 9): the page's one `--accent` fill stays "Link a
 * bank". Pressing it opens a confirmation first; confirming CLOSES the dialog and
 * only then starts the flow, so the dialog never sits over the bank's own window.
 * What the reconnect comes to — a toast, the match step, a different-bank or
 * repair dialog — is `useApplyRelinkOutcome`'s, shared with the Link prompt and
 * the OAuth return.
 */
export function ReconnectAction({
  group,
  entryPoint,
}: {
  group: InstitutionGroup
  entryPoint: 'connections' | 'accounts_panel'
}): ReactNode {
  const gated = useAddAccountGate()
  const reconnect = useReconnect(entryPoint)
  const [confirming, setConfirming] = useState(false)
  const [startRequested, setStartRequested] = useState(false)

  // Start only once the confirmation has actually closed.
  useEffect(() => {
    if (!startRequested || confirming) return
    setStartRequested(false)
    void reconnect.start(group)
  }, [startRequested, confirming, reconnect, group])

  const relinkId = relinkIdOf(group)
  if (relinkId === null) return null

  const busy = reconnect.pendingId !== null
  const n = group.accounts.length
  const chip = (
    <Button variant={RECONNECT_VARIANT} size={RECONNECT_SIZE} disabled={busy} aria-busy={busy ? true : undefined} className={busy ? 'reconnect-busy' : undefined} onClick={() => setConfirming(true)}>
      {busy ? 'Reconnecting…' : RECONNECT_LABEL}
    </Button>
  )

  return (
    <>
      {gated ? (
        <PremiumGate
          entitlement="unlimited_accounts"
          preview={
            <ButtonPreview variant={RECONNECT_VARIANT} size={RECONNECT_SIZE}>
              {RECONNECT_LABEL}
            </ButtonPreview>
          }
          label="reconnecting a bank"
        >
          {chip}
        </PremiumGate>
      ) : (
        chip
      )}
      <ConfirmDialog
        open={confirming}
        mode="reconnect_connection"
        title={`Reconnect ${group.name}?`}
        body={`You'll sign in to ${group.name} again. Your ${n} account${n === 1 ? '' : 's'} and their history carry on — nothing is duplicated.`}
        confirmLabel="Continue"
        onCancel={() => setConfirming(false)}
        onConfirm={async () => {
          setConfirming(false)
          setStartRequested(true)
        }}
      />
      {reconnect.dialogs}
    </>
  )
}
